import React, { Fragment, useState, useRef, useContext, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FetchSupportServiceNow, getUserEnterpriseId, getUserIdFromLocalStorage, validateStringForXSS } from '../utils';
import { APIContext } from '../contexts/APIContext';
import { useMsal } from '@azure/msal-react';
import { TooltipContent } from '@radix-ui/react-tooltip';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipArrow } from './ui/Tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import BounceLoader from './ui/Loaders/BounceLoader';
import { getValidToken } from '../utils';

export const FeedbackModal = (props: any) => {
  const {
    websocketConnected,
    websocketConnectionRef: { current: websocket },
    feedbackOptions,
    applicationName,
    displayName,
    requestNumber,
    setRequestNumber,
    WBUId,
  } = useContext(APIContext);
  const [selectedPurpose, setSelectedPurpose] = useState('');
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { setIsModalOpen, file, setFile } = props;
  const [onSubmit, setOnSubmit] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState('GMA'); // Default selected option
  // Ref for file input
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { accounts } = useMsal();
  const Options = feedbackOptions;

  // Check if all required fields are filled to enable the submit button
  const isSubmitEnabled = () => {
    return selectedPurpose === 'Give feedback' && selectedOption && description.trim() !== '' && !errorMessage;
  };

  const handlePurposeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPurpose(event.target.value);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (value.length > 4000) {
      setDescription(value);
    }
    const startsWithSpecialCharOrNumber = /^[^a-zA-Z]/.test(value);
    const hasInvalidCharacters = /[<>]/.test(value);
    if (startsWithSpecialCharOrNumber || hasInvalidCharacters) {
      setErrorMessage('Enter a valid description.');
    } else {
      setErrorMessage('');
    }
    setDescription(value);
  };

  const generateCorrelationId = (length: number) => {
    const array = new Uint8Array(length / 2); // Each byte generates 2 hex characters
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  };
  const fileToBase64 = async (file: File): Promise<string | ArrayBuffer | null> => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFile = async (file: File | null) => {
    let encodedBase64 = '';
    let fileName = '';
    let fileType = '';

    if (file) {
      try {
        const res = await fileToBase64(file);
        if (res !== null) {
          encodedBase64 = (res as string).split(',')[1];
        }
      } catch (err) {
        encodedBase64 = '';
      }

      fileName = file.name;
      fileType = file.type;
    }
    return [fileName, fileType, encodedBase64];
  };

  // Call the function with the file
  const handleSubmit = async () => {
    const token = await getValidToken();
    if (isSubmitEnabled()) {
      // Handle form submission
      setOnSubmit(true);
      const [fileName, fileType, encodedBase64] = await handleFile(file);
      const payload = {
        function: 'fetch_supportservicenow_ritm',
        IncidentsRequestJson: `{"requested_for":"${getUserEnterpriseId(
          accounts
        )}","correlation_id":"${generateCorrelationId(10)}","short_description":"${validateStringForXSS(
          description
        )}","u_categorization":"Other","u_category_ref":"","division":"${applicationName}","caller_id":"${getUserEnterpriseId(
          accounts
        )}","contact_type":"Self-service","assignment_group":"APPSUP-APP-GMA-FEEDBACK","u_owner_group":"APPSUP-APP-GMA-FEEDBACK","description":"${validateStringForXSS(
          description
        )}","state":"New","priority":"3","impact":"3","urgency":"3","assigned_to":"","location":"Bengaluru, BDC7A","u_contact":"1234567890","u_air_id":"${applicationName}",${
          file
            ? `"attachments":[{"payload":"${encodedBase64}","file_name":"${fileName}","content_type":"${fileType}","encoding":"base64"}]`
            : ''
        }}`,
        Type: 'SR',
        CorrelationUId: crypto.randomUUID(),
        CreatedByApp: applicationName,
        CreatedByUser: accounts[0].username,
        ModifiedByApp: applicationName,
        ModifiedByUser: getUserEnterpriseId(accounts),
        userId: getUserIdFromLocalStorage(),
        authorization: `Bearer ${token}`,
      };
      console.log('payload for request submission: ', payload);
      websocket?.send('SendMessage', payload);
    }
  };

  const toggleDropdown = (Name: string) => {
    setSelectedOption(Name);
    setDropdownVisible(false);
  };

  const handleRaiseAnotherRequest = () => {
    setSelectedPurpose('');
    setDescription('');
    setSelectedOption(displayName);
    setOnSubmit(false);
    setRequestNumber('');
    setFile(null);
  };

  const handleAttachFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setFile(files[0]);
    }
  };

  useEffect(() => {
    if (websocket && websocket.state === 'Connected') {
      (async () => {
        const token = await getValidToken();
        FetchSupportServiceNow(accounts, websocket, token);
      })();
    }
  }, [websocket, websocketConnected, accounts]);

  const getLastName = (fullName: string | undefined): string => {
    if (fullName) {
      const nameParts = fullName.split(' ');
      return nameParts[nameParts.length - 1];
    }
    return '';
  };

  /* truncate file name if length > 15 */
  const displayFileName = (name?: string): string => {
    if (!name) return '';

    const indexOfDot = name.lastIndexOf('.');
    const fileExtension = name.slice(indexOfDot);
    const baseName = name.slice(0, indexOfDot);

    return baseName.length > 15 ? `${baseName.slice(0, 15)}...${fileExtension}` : name;
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        {/* Opaque overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {onSubmit && requestNumber === '' ? (
            <div className="loading-container fixed top-0 left-0 z-[11] flex flex-col justify-center items-center h-full w-full bg-[#f1f1f180] backdrop-blur-[1px]">
              <BounceLoader />
              <p className="mt-6 text-lg">One Moment Please...</p>
            </div>
          ) : (
            <div className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
          )}
        </Transition.Child>

        {/* Modal panel */}
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-end">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <div
                className={`flex flex-col flex-shrink-0 ${
                  WBUId !== '' && window.self !== window.top ? 'mr-[14px]' : ''
                }`}
              >
                <Dialog.Panel className="relative flex border-t-4 border-t-[#A100FF] w-[547px] h-full transform flex-col bg-white font-graphik text-black transition-all">
                  {!onSubmit ? (
                    <div className="pt-4 flex flex-col flex-grow">
                      <img
                        className="ml-[32rem] cursor-pointer"
                        alt="close"
                        src="/icons/Close.svg"
                        onClick={() => setIsModalOpen(false)}
                      />
                      <div className="shadow-md pl-12 pb-6">
                        <Dialog.Title className="text-2xl">Request Help or Give Feedback</Dialog.Title>
                        <p className="mt-4 text-[13px] w-[425px]">
                          Hi {getLastName(accounts[0].name)}, thanks for reaching out to Accenture Reinvention Console
                          support team.
                        </p>
                      </div>
                      <div className="flex flex-col justify-between py-6 pl-12 pr-6">
                        <div>
                          <div className="mb-4">
                            <label className="block text-[13px] font-medium ">
                              Select your purpose for contacting support team
                            </label>
                            <div className="flex py-4 pr-6 gap-4 border-b">
                              <div className="flex items-center">
                                {Options?.map((option: any) => (
                                  <TooltipProvider key={option[1]}>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <div className="flex items-center" key={option[1]}>
                                          <input
                                            id={option[1]}
                                            name="purpose"
                                            type="radio"
                                            value={option[1]}
                                            checked={selectedPurpose === option[1]}
                                            onChange={handlePurposeChange}
                                            className="peer hidden"
                                            disabled={option[1] !== 'Give feedback'} // Disable all except "Give Feedback"
                                          />
                                          <div
                                            className={`h-4 w-4 border-2 rounded-full flex items-center justify-center ${
                                              option[1] !== 'Give feedback'
                                                ? 'border-gray-300 cursor-not-allowed'
                                                : 'cursor-pointer'
                                            } ${
                                              selectedPurpose === option[1]
                                                ? 'border-[#A100FF] bg-white'
                                                : 'border-gray-300'
                                            }`}
                                            onClick={() => {
                                              if (option[1] === 'Give feedback') {
                                                setSelectedPurpose(option[1]);
                                              }
                                            }}
                                          >
                                            {selectedPurpose === option[1] && (
                                              <div className="h-2 w-2 rounded-full bg-[#A100FF]"></div>
                                            )}
                                          </div>
                                          <label htmlFor={option[1]} className="mx-3 block text-sm text-gray-700">
                                            {option[1]}
                                          </label>
                                        </div>
                                      </TooltipTrigger>
                                      {option[1] !== 'Give feedback' ? (
                                        <TooltipContent
                                          side="top"
                                          className="bg-[#505050] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                                          sideOffset={5}
                                          align={'center'}
                                          alignOffset={0}
                                        >
                                          <p className="tooltip-content text-white text-xs capitalize not-italic">
                                            {`Coming Soon`}
                                          </p>
                                          <TooltipArrow width={11} height={5} style={{ fill: '#505050' }} />
                                        </TooltipContent>
                                      ) : (
                                        <TooltipContent
                                          side="top"
                                          className="bg-[white] border-0 rounded-md max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                                          sideOffset={5}
                                          align={'center'}
                                          alignOffset={0}
                                        >
                                          <p className="tooltip-content text-black text-xs capitalize not-italic">
                                            {`(Continuous improvement input for GMA (e.g. UX/UI features, navigation, search)`}
                                          </p>
                                          <TooltipArrow width={11} height={5} style={{ fill: 'white' }} />
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-between mr-12">
                            <label className="block text-sm text-gray-700">
                              I need help with <b className="text-sm">{selectedOption}</b>
                            </label>
                            <img
                              className={`w-3.5 cursor-pointer transition-all duration-300 ease-[ease] ${
                                dropdownVisible ? 'rotate-180 pink-filter-svg' : ''
                              }`}
                              src="/icons/Down.svg"
                              alt="arrow"
                              onClick={() => setDropdownVisible(!dropdownVisible)}
                            />
                          </div>
                          {dropdownVisible && (
                            <div className="absolute w-[59%] z-[1] mr-[68px] right-0 bg-white border shadow-md rounded">
                              <div className="flex flex-col">
                                <button
                                  className="px-4 py-2 text-left hover:bg-gray-100"
                                  onClick={() => toggleDropdown(displayName)}
                                >
                                  {displayName}
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="mb-4 mt-4">
                            <label className="text-xs text-black relative">
                              Describe your issue. Someone from the support team will reach out to you
                              <img
                                className="py-1 absolute right-[-6px] top-[-2px]"
                                width="4"
                                height="4"
                                src="/icons/mandatory.svg"
                                alt="mandatory"
                              />
                            </label>
                            <textarea
                              value={description}
                              onChange={handleDescriptionChange}
                              maxLength={4000}
                              className=" Scrollbar mt-1 pl-1 w-[460px] h-[88px] block w-full border-gray-300 shadow-sm focus:border-1px sm:text-sm"
                            ></textarea>
                            <div className="flex items-center">
                              {errorMessage && <div className="text-red-500 text-sm">{errorMessage}</div>}
                              <div className="text-sm text-gray-500 ml-auto">{description.length}/4000 characters</div>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <img
                                      className="cursor-pointer"
                                      alt="attach file"
                                      src="/icons/file.svg"
                                      onClick={handleAttachFileClick}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    className="bg-[white] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                                    sideOffset={8}
                                    align={'center'}
                                    alignOffset={0}
                                  >
                                    <p className="tooltip-content text-black text-xs capitalize not-italic">
                                      Attach File
                                    </p>
                                    <TooltipArrow width={14} height={9} style={{ fill: 'white' }} />
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <div className="flex items-center ml-2">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <p className="text-xs text-[#A100FF] mr-2 underline">
                                        {displayFileName(file?.name)}
                                      </p>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="bg-white border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                                      sideOffset={2}
                                    >
                                      <p className="tooltip-content text-black bg-white text-xs not-italic">
                                        {file?.name}
                                      </p>
                                      <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: 'white' }} />
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <input className="hidden" type="file" ref={fileInputRef} onChange={handleFileChange} />
                            </div>
                            {file && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <img
                                      onClick={() => setFile(null)}
                                      className="cursor-pointer text-[#A100FF]"
                                      src="/icons/Delete.svg"
                                    ></img>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    className="bg-[white] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                                    sideOffset={8}
                                    align={'center'}
                                    alignOffset={0}
                                  >
                                    <p className="tooltip-content text-black text-xs capitalize not-italic">Delete</p>
                                    <TooltipArrow width={14} height={9} style={{ fill: 'white' }} />
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4 mr-4 pb-2">
                          <button
                            className="border-[1px] border-black text-black px-4 py-2 hover:border-[#460073] hover:text-[#460073] "
                            onClick={() => setIsModalOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className={`bg-black text-white px-4 py-2 hover:bg-[#460073] ${
                              isSubmitEnabled() ? '' : 'opacity-50 cursor-not-allowed bg-[#CCCCCC]'
                            }`}
                            onClick={handleSubmit}
                            disabled={!isSubmitEnabled()}
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center pt-4">
                      <img
                        className="ml-[32rem] cursor-pointer"
                        alt="close"
                        src="/icons/Close.svg"
                        onClick={() => setIsModalOpen(false)}
                      />
                      <div className="py-16">
                        <div className="flex flex-col">
                          <img src="/icons/Checkmark.svg" alt="success" className="mb-4 w-16" />
                          <p className="text-sm">Thanks for contacting Accenture Reinvention Console Support.</p>
                          <p className="mt-4 text-sm">The issue has been raised</p>
                          <p className="mt-2 text-sm">Issue ID: {requestNumber}</p>
                          <button
                            className="mt-6 bg-black text-white px-4 py-2 h-[40px] text-sm w-[178px] hover:bg-[#460073]"
                            onClick={() => {
                              handleRaiseAnotherRequest();
                              setIsModalOpen(true);
                            }}
                          >
                            Raise another request
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default FeedbackModal;
